import React, { Fragment, useRef } from 'react';
import { useOnScreenTelemetry } from '@hooks/useOnScreenTelemetry';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import {
  Container,
  Description,
  DesktopPhoto,
  Divider,
  FeatureDetails,
  FeatureLink,
  FeaturePhoto,
  FeatureSpan,
  FeatureTitle,
  Icon,
  MobilePhoto,
  StyledText,
  WrapperDiv,
} from './NavigationSection.styles';
import { useTranslation } from 'react-i18next';

const SECTION_ID = 'FindAnEvent_Navigation_Section';

type Feature = {
  id: string;
  to: string;
  label: string;
  desc: string;
  assetDesktop?: string;
  assetMobile: string;
  showInMobile?: boolean;
};

export const NavigationSection: React.FC = () => {
  const navigationSectionRef = useRef<HTMLDivElement>(null);
  const isMobile = useThemeMediaQuery('below_sm');

  useOnScreenTelemetry(navigationSectionRef, SECTION_ID);

  const { t } = useTranslation('home');
  const featureItems: Feature[] = [
    {
      id: 'home-features-to-wedding-website',
      to: '/wedding-website',
      label: t('sections.features.weddingWebsite.listItemLabel'),
      desc: t('sections.features.weddingWebsite.desc'),
      assetDesktop: 'find-an-event/planning_section_wedding_website.jpg',
      assetMobile: 'find-an-event/planning_section_wedding_website_mobile.jpg',
    },
    {
      id: 'home-features-to-guest-list',
      to: '/guest-list',
      label: t('sections.features.guestList.listItemLabel'),
      desc: t('sections.features.guestList.desc'),
      assetDesktop: 'find-an-event/planning_section_guest_list.jpg',
      assetMobile: 'find-an-event/planning_section_guest_list_mobile.jpg',
    },
    {
      id: 'home-features-save-the-dates',
      to: '/cards/wedding/save-the-date',
      label: t('sections.features.saveTheDates.listItemLabel'),
      desc: t('sections.features.saveTheDates.desc'),
      assetDesktop: 'find-an-event/planning_section_save_the_dates.jpg',
      assetMobile: 'find-an-event/planning_section_save_the_dates_mopbile.jpg',
    },
    {
      id: 'home-features-to-invitations',
      to: '/wedding-invitations',
      label: t('sections.features.invitations.listItemLabel'),
      desc: t('sections.features.invitations.desc'),
      assetDesktop: 'find-an-event/planning_section_invitations.jpg',
      assetMobile: 'find-an-event/planning_section_invitations_mobile.jpg',
    },
    {
      id: 'home-features-to-smart-rsvp',
      to: '/online-rsvp',
      label: t('sections.features.smartRSVP.listItemLabel'),
      desc: t('sections.features.smartRSVP.desc'),
      assetDesktop: 'find-an-event/planning_section_smart_rsvp.jpg',
      assetMobile: 'find-an-event/planing_section_smart_rsvp_mobile.jpg',
    },
    {
      id: 'home-features-to-registry',
      to: '/registry',
      label: t('sections.features.registry.listItemLabel'),
      desc: t('sections.features.registry.desc'),
      assetDesktop: 'find-an-event/planning_section_wedding_registry.jpg',
      assetMobile: 'find-an-event/planning_section_wedding_registry_mobile.jpg',
    },
    {
      id: 'home-features-to-guest-app',
      to: '/app',
      label: t('sections.features.guestApp.listItemLabel'),
      desc: t('sections.features.guestApp.desc'),
      showInMobile: true,
      assetMobile: 'find-an-event/planning_section_mobile_app_mobile.jpg',
    },
  ];

  const { navigationSection } = t('findAnEvent', { ns: 'findAnEvent' });
  return (
    <WrapperDiv id={SECTION_ID} ref={navigationSectionRef}>
      <StyledText tagName="h2" variant="hed56" color="neutra6">
        {navigationSection.title}
      </StyledText>
      <Container>
        {featureItems.map((feature, index) => {
          const { id, to, label, desc, assetMobile, assetDesktop, showInMobile } = feature;

          if (!isMobile && showInMobile) {
            return null;
          }

          return (
            <Fragment key={label + id + index}>
              {index % 2 === 0 && index > 0 && <Divider />}
              <FeatureLink href={to} id={id} target="_blank" aria-labelledby={label}>
                <FeatureSpan>
                  <FeaturePhoto>
                    <MobilePhoto alt={assetMobile} key={assetMobile} src={assetMobile} width="100%" />
                    {assetDesktop && <DesktopPhoto alt={assetDesktop} key={assetDesktop} src={assetDesktop} width="100%" />}
                  </FeaturePhoto>
                  <FeatureDetails>
                    <FeatureTitle variant="hed40" color="mono1" tagName="span" textAlign="center">
                      {label}
                      <Icon name="rightArrow" color="yellow6" size={24} />
                    </FeatureTitle>
                    <Description variant="featureCopy17" color="mono1" tagName="span" textAlign="left">
                      {desc}
                    </Description>
                  </FeatureDetails>
                </FeatureSpan>
              </FeatureLink>
            </Fragment>
          );
        })}
      </Container>
    </WrapperDiv>
  );
};
